<template>
  <div class="timepicker w-full">
    <Dropdown
      ref="dropdownRef"
      :is-context-menu="false"
      :close-on-click-away="true"
      :close-on-click-within="false"
      position="bottom-start"
      class="w-full"
    >
      <template #triggerContent>
        <Input
          :id="id"
          :model-value="displayValue"
          icon="clock"
          readonly
          :readonly-has-styling="false"
          :has-error="hasError"
          :placeholder="placeholder"
          :alt="alt"
        />
      </template>

      <li class="timepicker__panel">
        <div
          ref="hoursColumn"
          class="timepicker__column"
        >
          <button
            v-for="hour in hours"
            :key="hour"
            type="button"
            class="timepicker__option"
            :class="{ 'is-selected': hour === selectedHour }"
            @click.stop="onHourClick(hour)"
          >
            {{ hour }}
          </button>
        </div>
        <div
          ref="minutesColumn"
          class="timepicker__column"
        >
          <button
            v-for="minute in minutes"
            :key="minute"
            type="button"
            class="timepicker__option"
            :class="{ 'is-selected': minute === selectedMinute }"
            @click.stop="onMinuteClick(minute)"
          >
            {{ minute }}
          </button>
        </div>
      </li>
    </Dropdown>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { computed, ref } from 'vue'
// components
import Dropdown from '@/components/dropdown/Dropdown.vue'
import Input from '@/components/forms/Input.vue'

dayjs.extend(customParseFormat)

/** Props for the time-only picker */
const props = withDefaults(
  defineProps<{
    /** Selected time as 'HH:mm:ss' (or 'HH:mm'), null when empty */
    modelValue?: string | null
    /** Marks the trigger input as invalid */
    hasError?: boolean
    /** Minute list granularity */
    minuteStep?: number
    /** Placeholder shown when no time is selected */
    placeholder?: string | null
    /** Input element id */
    id?: string | null
    /** Accessible label for the trigger input */
    alt?: string
  }>(),
  {
    modelValue: null,
    hasError: false,
    minuteStep: 5,
    placeholder: null,
    id: null,
    alt: 'Time'
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: string]
  select: [value: string]
}>()

const dropdownRef = ref<InstanceType<typeof Dropdown> | null>(null)

/** Hour options, '00'..'23' */
const hours = Array.from({ length: 24 }, (_, index) => String(index).padStart(2, '0'))

/** Minute options at the configured step */
const minutes = computed(() =>
  Array.from({ length: Math.ceil(60 / props.minuteStep) }, (_, index) =>
    String(index * props.minuteStep).padStart(2, '0')
  )
)

/** Currently selected hour ('HH'), null when empty */
const selectedHour = computed<string | null>(() => {
  // No selection yet
  if (!props.modelValue) return null
  const parsed = dayjs(props.modelValue, ['HH:mm:ss', 'HH:mm', 'H:mm'])
  return parsed.isValid() ? parsed.format('HH') : null
})

/** Currently selected minute ('mm'), null when empty */
const selectedMinute = computed<string | null>(() => {
  if (!props.modelValue) return null
  const parsed = dayjs(props.modelValue, ['HH:mm:ss', 'HH:mm', 'H:mm'])
  return parsed.isValid() ? parsed.format('mm') : null
})

/** Human display in the trigger input, 'HH:mm' */
const displayValue = computed(() => {
  // Empty state shows the placeholder instead
  if (!selectedHour.value || selectedMinute.value === null) return ''
  return `${selectedHour.value}:${selectedMinute.value}`
})

/**
 * Emits a new time value in strict 'HH:mm:ss' format.
 */
function emitTime(hour: string, minute: string): void {
  const value = `${hour}:${minute}:00`
  emit('update:modelValue', value)
  emit('select', value)
}

/**
 * Selecting an hour keeps the panel open so the minute can be picked next.
 */
function onHourClick(hour: string): void {
  emitTime(hour, selectedMinute.value ?? '00')
}

/**
 * Selecting a minute completes the choice and closes the panel.
 */
function onMinuteClick(minute: string): void {
  emitTime(selectedHour.value ?? '00', minute)
  dropdownRef.value?.closeDropdown()
}
</script>

<style>
.timepicker .dropdown-wrapper {
  display: block;
  width: 100%;
}

.timepicker .dropdown-wrapper > div {
  width: 100%;
}

.timepicker__panel {
  display: flex;
  gap: var(--s-1);
  list-style: none;
}

.timepicker__column {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 220px;
  overflow-y: auto;
  min-width: 56px;
  padding-right: var(--s-1);
}

.timepicker__option {
  cursor: pointer;
  padding: var(--s-1) var(--s-3);
  border-radius: var(--rounded-md);
  font-size: var(--text-sm);
  text-align: center;
  background: transparent;
  border: none;
  color: inherit;
}

.timepicker__option:hover {
  background-color: var(--bg-primary_hover);
}

.timepicker__option.is-selected {
  background-color: var(--fg-brand);
  color: var(--text-white);
  font-weight: 600;
}
</style>
